// ServiceNow Classic incident-record popup, styled to match a real ServiceNow
// incident form (grey toolbar, teal field labels, two-column layout, yellow
// Work notes, Activity timeline). Two callers: TeamsPage.tsx's "Triage"
// button on a Major Incidents alert card, and InboxPage.tsx's email-triage
// recommendations (via triageEngine.ts's window.openServiceNowTicket
// bridge). Every field beyond title/createdAt/isResolved is an optional
// override with an outage-flavored default, so the Teams call site (which
// predates this generalization) needs no changes. Fully local/decorative
// otherwise: no backend call, no real ticket is created, Update/Resolve/
// Delete just close the popup — the one REAL network call is the AI
// handoff-notes generator below, which hits the same POST /api/ai/chat
// route MIMPage.tsx's "Regenerate" notification button uses.
import { useEffect, useState } from 'react';
import { pbAuthedFetch } from '../authHeaders';
import { renderAiText, aiErrorMessage } from '../lib/aiText';
import '../styles/servicenow-ticket.css';

export interface TicketIncident {
  title: string;
  createdAt?: string;
  isResolved: boolean;
  caller?: string;
  openedBy?: string;
  category?: string;
  configurationItem?: string;
  location?: string;
  impact?: string;
  urgency?: string;
  priority?: string;
  contactType?: string;
  assignmentGroup?: string;
  changedFields?: string;
  workNotes?: string;
  activityNote?: string;
}

function ticketNumber(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `INC${String(h % 10000000).padStart(7, '0')}`;
}

function formatOpened(iso: string | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function IcoPaperclip() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M10.5 4.2 5.7 9a2 2 0 0 0 2.8 2.8l4.3-4.3a3.3 3.3 0 0 0-4.7-4.6L3.8 7.1a4.6 4.6 0 0 0 6.5 6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoInfo() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <circle cx="8" cy="8" r="6.3" />
      <path d="M8 7.2v3.6M8 5.2v.01" strokeLinecap="round" />
    </svg>
  );
}
function IcoEnvelope() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <rect x="2" y="3.5" width="12" height="9" rx="1" />
      <path d="M2.5 4.2 8 8.5l5.5-4.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoPulse() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M1.5 8.5h3l1.5-4 2.5 7 1.5-3h4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoList() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M2 4h12M2 8h12M2 12h12" strokeLinecap="round" />
    </svg>
  );
}
function IcoGear() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 2.3v1.4M8 12.3v1.4M13.7 8h-1.4M3.7 8H2.3M12 4l-1 1M5 11l-1 1M12 12l-1-1M5 5 4 4" strokeLinecap="round" />
    </svg>
  );
}
function IcoChevronDown() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3.5 6 8 10.5 12.5 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoLock() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <rect x="3.5" y="7" width="9" height="6.5" rx="1" />
      <path d="M5.2 7V4.8a2.8 2.8 0 0 1 5.6 0V7" />
    </svg>
  );
}
function IcoPerson() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <circle cx="8" cy="5.3" r="2.3" />
      <path d="M2.8 13.2c.7-2.6 2.6-4 5.2-4s4.5 1.4 5.2 4" strokeLinecap="round" />
    </svg>
  );
}
function IcoMinus() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 8h10" strokeLinecap="round" />
    </svg>
  );
}
function IcoFilter() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M2.5 3.5h11L9.5 8.7V13l-3-1.5V8.7z" strokeLinejoin="round" />
    </svg>
  );
}
function IcoSpark() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 1.5l1.5 4 4 1.5-4 1.5L8 12.5 6.5 8.5 2.5 7l4-1.5z" />
    </svg>
  );
}

function Field({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`sn-field${wide ? ' sn-field--wide' : ''}`}>
      <label className="sn-field-label">{label}</label>
      <div className="sn-field-value">{value || ' '}</div>
    </div>
  );
}

export function ServiceNowTicketModal({ incident, onClose }: { incident: TicketIncident; onClose: () => void }) {
  const number = ticketNumber(incident.title + (incident.createdAt || ''));
  const shortDescription = incident.title.replace(/^🔴\s*/, '');
  const caller = incident.caller || 'Atlas Incident Bot';
  const openedBy = incident.openedBy || 'Atlas Incident Bot';
  const category = incident.category || 'Outage';
  const configurationItem = incident.configurationItem || '';
  const location = incident.location || '';
  const impact = incident.impact || '1 - High';
  const urgency = incident.urgency || '1 - High';
  const priority = incident.priority || '1 - Critical';
  const contactType = incident.contactType || 'Automated';
  const assignmentGroup = incident.assignmentGroup || 'Major Incident Response';
  const changedFields = incident.changedFields || `Impact, Priority, Urgency${incident.isResolved ? ', State' : ''}`;
  const workNotes = incident.workNotes || '';
  const activityNote = incident.activityNote || '';

  const [aiState, setAiState] = useState<{ loading: boolean; error: string | null; summary: string | null }>({
    loading: false,
    error: null,
    summary: null,
  });

  async function generateAiSummary() {
    setAiState({ loading: true, error: null, summary: aiState.summary });
    const prompt = [
      "You are helping populate a ServiceNow incident ticket's handoff notes for whoever picks this up next.",
      '',
      `Ticket: ${shortDescription}`,
      `Category: ${category}`,
      `Impact / Priority: ${impact} / ${priority}`,
      configurationItem ? `Affected service: ${configurationItem}` : '',
      location ? `Site: ${location}` : '',
      `Current status / work notes: ${workNotes || '(none provided)'}`,
      '',
      'Write a concise handoff in exactly this structure:',
      'Summary: <1-2 sentence summary of the issue and its impact>',
      'Background: <2-3 sentences on the likely affected service/context and why this kind of issue happens>',
      'Next steps:',
      '1. <step>',
      '2. <step>',
      '3. <step>',
      'Keep it tight — this goes directly into a ticket work-notes field, not a report.',
    ].filter(Boolean).join('\n');

    try {
      const res = await pbAuthedFetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.reply) {
        setAiState({ loading: false, error: aiErrorMessage(res.status, data), summary: null });
        return;
      }
      setAiState({ loading: false, error: null, summary: String(data.reply).trim() });
    } catch {
      setAiState({ loading: false, error: 'AI request failed — check your connection and try again.', summary: null });
    }
  }

  // Auto-generate handoff notes on open rather than waiting for a manual
  // click — whoever opens this ticket should see them already there.
  useEffect(() => {
    generateAiSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sn-overlay" onClick={onClose}>
      <div className="sn-card" onClick={(e) => e.stopPropagation()}>
        <div className="sn-toolbar">
          <div className="sn-toolbar-left">
            <button className="sn-icobtn" onClick={onClose} title="Back" aria-label="Back">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 3 5 8l5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button className="sn-icobtn" title="Menu" aria-label="Menu">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 4h12M2 8h12M2 12h12" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div className="sn-toolbar-icons">
            <IcoPaperclip /><IcoInfo /><IcoEnvelope /><IcoPulse /><IcoList /><IcoGear />
          </div>
        </div>

        <div className="sn-titlebar">
          <div className="sn-title">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true"><path d="M4 12 12 4M6 2l2 2-4 4-2-2zM10 10l2 2-2 2-2-2z" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Incident - {number}
          </div>
          <div className="sn-title-actions">
            <button className="sn-btn" onClick={onClose}>Update</button>
            <button className="sn-btn sn-btn--primary" onClick={onClose}>Resolve Incident</button>
            <button className="sn-btn sn-btn--danger" onClick={onClose}>Delete</button>
          </div>
        </div>

        <div className="sn-body">
          <div className="sn-form">
            <div className="sn-form-col">
              <Field label="Number" value={number} />
              <Field label="Caller" value={caller} />
              <Field label="Location" value={location} />
              <Field label="Category" value={category} />
              <Field label="Subcategory" value="-- None --" />
              <Field label="Configuration item" value={configurationItem} />
              <Field label="Impact" value={impact} />
              <Field label="Urgency" value={urgency} />
              <Field label="Priority" value={priority} />
              <Field label="Short description" value={shortDescription} wide />
            </div>
            <div className="sn-form-col">
              <Field label="Opened" value={formatOpened(incident.createdAt)} />
              <Field label="Opened by" value={openedBy} />
              <Field label="Contact type" value={contactType} />
              <Field label="State" value={incident.isResolved ? 'Resolved' : 'Active'} />
              <Field label="Assignment group" value={assignmentGroup} />
              <Field label="Assigned to" value="" />
            </div>
          </div>

          <div className="sn-notes">
            <div className="sn-notes-head">
              <IcoChevronDown />
              <span>Notes</span>
            </div>
            <div className="sn-notes-row">
              <span className="sn-notes-row-label">Watch list</span>
              <span className="sn-icobtn sn-icobtn--sm"><IcoLock /></span>
              <span className="sn-icobtn sn-icobtn--sm"><IcoPerson /></span>
            </div>
            <div className="sn-notes-field">
              <label className="sn-field-label">Additional comments (Customer visible)</label>
              <div className="sn-textarea" />
            </div>
            <div className="sn-notes-field">
              <label className="sn-field-label">Work notes</label>
              <div className="sn-textarea sn-textarea--worknotes">{renderAiText(workNotes)}</div>
            </div>

            <div className="sn-ai">
              <div className="sn-ai-head">
                <span className="sn-ai-label"><IcoSpark /> AI-Generated Handoff Notes</span>
                <button
                  type="button"
                  className="sn-ai-btn"
                  onClick={generateAiSummary}
                  disabled={aiState.loading}
                >
                  {aiState.loading ? 'Generating…' : aiState.summary ? '↻ Regenerate' : <><IcoSpark /> Generate AI Summary</>}
                </button>
              </div>
              {aiState.error && <div className="sn-ai-error">{aiState.error}</div>}
              {aiState.summary && <div className="sn-ai-summary">{renderAiText(aiState.summary)}</div>}
            </div>

            <div className="sn-activity">
              <div className="sn-activity-head">
                <IcoMinus />
                <span>Activity</span>
                <span className="sn-activity-spacer" />
                <IcoFilter />
                <IcoChevronDown />
              </div>
              <div className="sn-activity-entry">
                <div className="sn-activity-meta">
                  <span className="sn-activity-time">{formatOpened(incident.createdAt)}</span>
                  <span className="sn-activity-who">{openedBy}</span>
                  <span className="sn-activity-changed">Changed: {changedFields}</span>
                </div>
                <div className="sn-activity-empty">Assigned to: (Empty)</div>
                {activityNote && <div className="sn-activity-comment">{activityNote}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
